import React from 'react';
import qs from 'query-string';
import memoize from 'lodash/memoize';
import uniqBy from 'lodash/uniqBy';
import * as contentful from 'contentful';
import { gray50, white } from '@spotify-internal/encore-web';
export { ContentfulImage } from './ContentfulImage';
const PAGE_SIZE = 10;
export const lightCaptionStyle = {
    position: 'relative',
    color: gray50,
    background: white,
    textAlign: 'center',
};
const getPreviewAPI = () => {
    return contentful.createClient({
        host: process.env.REACT_APP_CONTENTFUL_PREVIEW_HOST,
        environment: process.env.REACT_APP_CONTENTFUL_ENVIRONMENT,
        space: process.env.REACT_APP_CONTENTFUL_SPACE || '',
        accessToken: process.env.REACT_APP_CONTENTFUL_PREVIEW_TOKEN,
        resolveLinks: true,
        retryOnError: false,
    });
};
const getAPI = memoize(() => {
    return contentful.createClient({
        host: process.env.REACT_APP_CONTENTFUL_HOST,
        environment: process.env.REACT_APP_CONTENTFUL_ENVIRONMENT,
        space: process.env.REACT_APP_CONTENTFUL_SPACE || '',
        accessToken: process.env.REACT_APP_CONTENTFUL_TOKEN,
        resolveLinks: true,
        retryOnError: false,
    });
});
const getSearchAPI = memoize(() => {
    return contentful.createClient({
        host: process.env.REACT_APP_CONTENTFUL_HOST,
        environment: process.env.REACT_APP_CONTENTFUL_ENVIRONMENT,
        space: process.env.REACT_APP_CONTENTFUL_SPACE || '',
        accessToken: process.env.REACT_APP_CONTENTFUL_TOKEN,
        resolveLinks: false,
        retryOnError: true,
        timeout: 10000,
    });
});
export async function fetchEntry(props) {
    const data = await fetchEntryList({
        ...props,
        limit: 1,
        skip: 0,
    });
    const entries = data.entries;
    if (!entries || !entries.length) {
        return null;
    }
    else if (entries.length > 1) {
        console.error(`${entries.length} items returned for ${props.contentType} query`);
    }
    return entries[0];
}
export async function fetchEntryList({ contentType, order, filters = {}, limit = 10, skip = 0, filterNulls = true, select, query, locale, preview = false, linksToEntry, }) {
    const params = {
        limit,
        skip,
        order,
        select,
        query,
        locale,
        links_to_entry: linksToEntry,
    };
    for (const [key, val] of Object.entries(filters)) {
        if (val !== null) {
            if (key.startsWith('sys')) {
                params[key] = val;
            }
            else {
                params[`fields.${key}`] = val;
            }
        }
    }
    return apiFetchEntries(contentType, params, filterNulls, preview);
}
async function apiFetchEntries(contentType = '', params = {}, filterNulls = true, preview = false) {
    const fetchParams = { include: 3, ...params };
    if (contentType) {
        if (Array.isArray(contentType)) {
            fetchParams['sys.contentType.sys.id[in]'] = contentType.join(',');
        }
        else {
            fetchParams.content_type = contentType;
        }
    }
    let api;
    if (preview) {
        api = await getPreviewAPI();
    }
    else if (params.query) {
        api = await getSearchAPI();
    }
    else {
        api = await getAPI();
    }
    const entries = await api.getEntries(fetchParams);
    if (filterNulls) {
        entries.items = entries.items.filter((e) => e !== null && Boolean(e.sys.contentType));
    }
    const circularJsonSafeEntries = entries.stringifySafe();
    const parsedEntires = JSON.parse(circularJsonSafeEntries);
    const items = parsedEntires.items.map((entry) => {
        var _a;
        return expand(entry.sys.id, ((_a = entry.sys.contentType) === null || _a === void 0 ? void 0 : _a.sys.id) || '', entry.fields);
    });
    return {
        limit: entries.limit,
        skip: entries.skip,
        total: entries.total,
        entries: items,
        totalUnfiltered: entries.total,
    };
}
const isContentful = (type, v) => { var _a; return ((_a = v === null || v === void 0 ? void 0 : v.sys) === null || _a === void 0 ? void 0 : _a.type) === type; };
export function expand(id, ctype, fields) {
    if (!fields) {
        return fields;
    }
    fields.id = id;
    fields.contentType = ctype;
    for (const [key, val] of Object.entries(fields)) {
        if (val === null || val === undefined) {
            continue;
        }
        else if (Array.isArray(val)) {
            fields[key] = val.map(v => {
                var _a, _b;
                return typeof v === 'object' && v.sys && v.fields && ((_b = (_a = v.sys) === null || _a === void 0 ? void 0 : _a.contentType) === null || _b === void 0 ? void 0 : _b.sys)
                    ? expand(v.sys.id, v.sys.contentType.sys.id, v.fields)
                    : v;
            });
        }
        else if (isContentful('Entry', val)) {
            fields[key] = expand(val.sys.id, val.sys.contentType.sys.id, val.fields);
        }
        else if (isContentful('Asset', val)) {
            const asset = val.fields;
            fields[key] = asset;
        }
    }
    for (const [key, val] of Object.entries(fields)) {
        const [arrKey, indexKey, propKey] = key.split('__');
        if (arrKey && indexKey && propKey) {
            const i = parseInt(indexKey, 10) - 1;
            const arr = fields[arrKey] || [];
            arr[i] = arr[i] || {};
            arr[i][propKey] = val;
            fields[arrKey] = arr;
            delete fields[key];
        }
    }
    return fields;
}
export function makeWebpUrl(imgUrl, quality = 80) {
    const { url, query } = qs.parseUrl(imgUrl);
    delete query.fl;
    query.fm = 'webp';
    query.q = `${quality}`;
    return `${url}?${qs.stringify(query)}`;
}
export function makeJpgUrl(imgUrl, fl, quality) {
    const { url, query } = qs.parseUrl(imgUrl);
    query.fm = 'jpg';
    if (fl)
        query.fl = `${fl}`;
    if (quality)
        query.q = `${quality}`;
    return `${url}?${qs.stringify(query)}`;
}
export function makeImageSizeUrl(imgUrl, width, height, fit, focus) {
    const { url, query } = qs.parseUrl(imgUrl);
    if (width !== undefined && !isNaN(width))
        query.w = `${width}`;
    if (height !== undefined && !isNaN(height))
        query.h = `${height}`;
    if (fit)
        query.fit = `${fit}`;
    if (focus)
        query.f = `${focus}`;
    return `${url}?${qs.stringify(query)}`;
}
export function makeContentfulAsset({ url, contentType, width, height, size = -1, description = '', }) {
    return {
        file: {
            contentType,
            url,
            details: {
                size,
                image: {
                    width,
                    height,
                },
            },
        },
        description,
        contentType,
    };
}
export function usePagedEntryList(query) {
    const [entries, setEntries] = React.useState();
    const [pageNum, setPageNum] = React.useState(0);
    const [loading, setLoading] = React.useState(true);
    const page = React.useRef(null);
    const pageSize = query.limit || PAGE_SIZE;
    const hasNextPage = () => (!page.current && true) || pageNum * pageSize < page.current.total;
    const getNextPage = async () => {
        if (!hasNextPage)
            return;
        const nextPageNum = pageNum + 1;
        const q = {
            ...query,
            skip: (nextPageNum - 1) * pageSize,
        };
        setLoading(true);
        fetchEntryList(q).then(data => {
            const newEntries = [...(entries || []), ...data.entries].filter(Boolean);
            page.current = data;
            setEntries(uniqBy(newEntries, 'id'));
            setPageNum(nextPageNum);
            setLoading(false);
        });
    };
    React.useEffect(() => {
        getNextPage();
    }, [pageNum]);
    return {
        entries,
        page,
        pageNum,
        pageSize,
        loading,
        hasNextPage,
        getNextPage,
    };
}
